<template>
  <div id="map-wrapper" class="z4">
    <div class="map-loading caps">Loading...</div>
    <div id="map" class="z4" v-if="investigations"></div>
    <close-button :onClick="toggleMap"></close-button>
  </div>
</template>

<script>
import 'mapbox-gl/src/css/mapbox-gl.css'
import CloseButton from '@/components/CloseButton'
import mapboxgl from 'mapbox-gl'
import { incidentDates, isStudy } from '@/utils'
import { mapActions, mapState, mapGetters } from 'vuex'

export default {

  name: 'Map',

  data () {
    return {
      mapboxToken: 'pk.eyJ1IjoiZmEtd2ViIiwiYSI6ImNqczJiZXJjOTA1NGs0Ym54cTB0NTAyNGQifQ.EjulB2cnGZgW6RqeNne7Hw',
      map: false
    }
  },

  components: {
    CloseButton
  },

  mounted: function () {
    this.$nextTick(function () {
      this.initMap()
    })
  },

  computed: {
    ...mapState([
      'investigations',
      'mapLocations',
    ]),
    ...mapGetters([
      'getInvestigationBySlug'
    ])
  },

  watch: {
    'investigations' () {
      this.$nextTick(function () {
        this.initMap()
      })
    }
  },

  methods: {
    ...mapActions([
      'toggleMap'
    ]),
    initMap() {
      if (!this.map) {
        mapboxgl.accessToken = this.mapboxToken

        this.map = new mapboxgl.Map({
          container: 'map',
          style: 'mapbox://styles/fa-web/cjs50kubo136r1foftea5juec',
          center: [25, 30],
          zoom: 1.9 * window.innerWidth / 1920
        })

        let self = this

        this.mapLocations.forEach(function(location) {
          if (location.location !== "") {
            let inv = self.getInvestigationBySlug(location.slug)
            const isInvestigation = !isStudy(inv)
            let el = document.createElement('div')
            el.className = isInvestigation ? 'marker red' : 'marker yellow'
            el.dataset.reference = inv.id

            if (location.location.lng && location.location.lat) {
              new mapboxgl.Marker(el)
                .setLngLat([location.location.lng, location.location.lat])
                .setPopup(new mapboxgl.Popup({ offset: 12 }) // add popups
                .setHTML(`
                  <a href="/investigation/${inv.slug}">
                    <h3 class="caps mb0"><span class='red'>i.${inv.code}</span>&nbsp;${inv.title}</h3>
                    <video class="mt1" data-reference="${inv.id}" data-src="${inv.preview_video.link}" muted autoplay loop></video>
                    <div class="clearfix border xs-hide">
                      <div class="col col-6 p2">
                        <div class='caps fs-xs m0 label'>Date of Incident</div>
                        ${incidentDates(inv)}
                      </div>
                      <div class="col col-6 border-left p2 xs-hide">
                        <div class='caps fs-xs m0 label'>Location</div>
                        ${inv.location_title}
                      </div>
                    </div>
                    <div class="border-left border-bottom border-right p2 xs-hide">
                      ${inv.abstract}
                    </div>
                  </a>
                `)
                .on('open', function(e) {
                  console.log(e)
                  let vid = e.target._content.getElementsByTagName('video')[0]
                  vid.setAttribute('src', vid.dataset.src)
                }).on('close', function(e) {
                  let vid = e.target._content.getElementsByTagName('video')[0]
                  vid.setAttribute('src', '')
                }))
                .addTo(self.map)
            }
          }
        })
      }
    }
  }
}
</script>

<style lang="css">
  @keyframes fadein{
      0% { opacity:0; }
      60% { opacity:0; }
      100% { opacity:1; }
  }

  @-webkit-keyframes fadein{
      0% { opacity:0; }
      60% { opacity:0; }
      100% { opacity:1; }
  }

  #map-wrapper {
    background: var(--black);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10000;

    .close-button {
      z-index: 10001;
    }
  }

  .map-loading {
    position: fixed;
    top: 49%;
    width: 100%;
    color: var(--red);
    letter-spacing: 0;
    text-align: center;
  }

  #map {
    -webkit-animation: 1.5s ease-out 0s normal forwards 1 fadein;
    animation: 1.5s ease-out 0s normal forwards 1 fadein;
    background: var(--black);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;

    h1, h2, h3, h4, h5, h6 {
      font-size: var(--font-size-s);
      line-height: 1;
      font-weight: regular;
      font-size: 1em;
    }

    .marker {
      background-size: cover;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      cursor: pointer;

      &:hover {
        border: 2px solid white;
      }
    }

    .marker.red {
      background-image: url('../assets/marker-red.svg');
    }

    .marker.yellow {
      background-image: url('../assets/marker-yellow.svg')
    }

    .mapboxgl-popup-content {
      font-family: var(--font-sans);
      font-size: var(--font-size-s);
      line-height: 1.3;
      background: var(--col-light);
      border-radius: 0;
      padding: 1.5em;
      width: 30vw;
      max-width: 530px;

      .mapboxgl-popup-close-button {
        display: none;
      }

      video {
        max-width: 100%;
        width: 100%;
        +div {
          margin-top: -4px;
        }
      }

      .label {
        margin-bottom: 0.3em;
      }
    }

    .mapboxgl-popup-tip {
      border-top-color: var(--col-light);
    }

    .mapboxgl-ctrl-bottom-right {
      font-size: 8px;
      line-height: 1.4;
    }
  }

  @media (--phone) {
    #map {
      .marker {
        width: 18px;
        height: 18px;
      }
      .mapboxgl-popup-content {
        padding: 0.75em;

        video {
          /*display: none;*/
        }
      }
    }
  }
</style>
